import { useState } from 'react';
import { CreditCard, Pencil, Plus } from 'lucide-react';
import { useSession } from '@/api/session';
import { useDrivers, useVehicles } from '@/api/hooks/catalog';
import { useFuelCards, useSaveFuelCard, type FuelCard } from '@/api/hooks/finance';
import { formatDate, daysUntil } from '@/lib/format';
import { Badge } from '@/components/ui/Badge';
import { Button, IconButton } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { Checkbox, DatePicker, Field, Input, Select } from '@/components/ui/Field';
import { LoadingBlock } from '@/components/ui/State';
import { Table, TBody, TD, TH, THead, TR, TableEmpty } from '@/components/ui/Table';
import { toast } from '@/stores/toast';

const PROVIDERS = [
  { value: 'as24', label: 'AS 24' },
  { value: 'dkv', label: 'DKV' },
  { value: 'shell', label: 'Shell' },
  { value: 'uta', label: 'UTA' },
  { value: 'e100', label: 'E100' },
  { value: 'other', label: 'Друга' },
];
const PROVIDER_LABEL = Object.fromEntries(PROVIDERS.map((p) => [p.value, p.label]));

/** „7077123456789012345" → „7077 •••• 2345": на екрана стига началото и краят. */
function masked(number: string): string {
  return number.length > 8 ? `${number.slice(0, 4)} •••• ${number.slice(-4)}` : number;
}

/**
 * Горивните карти — по номера на картата импортът от AS 24 намира буса, курса
 * и шофьора. Карта без бус дава „непозната карта" при всеки импорт, затова
 * регистърът стои на екрана с горивото, не скрит в настройките.
 */
export function FuelCardsCard() {
  const role = useSession((st) => st.user?.role);
  const canEdit = role === 'owner' || role === 'dispatcher';
  const [showInactive, setShowInactive] = useState(false);
  const cards = useFuelCards(showInactive);
  const [editing, setEditing] = useState<FuelCard | 'new' | null>(null);

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title={
          <span className="flex items-center gap-1.5">
            <CreditCard className="size-4 text-ink-dim" />
            Горивни карти
          </span>
        }
        subtitle="Всяка карта към бус — по нея зарежданията от извлечението намират буса и курса."
        actions={
          <span className="flex items-center gap-3">
            <Checkbox label="И спрените" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            {canEdit ? (
              <Button size="sm" variant="secondary" icon={<Plus className="size-3.5" />} onClick={() => setEditing('new')}>
                Карта
              </Button>
            ) : null}
          </span>
        }
      />
      {cards.isLoading ? (
        <LoadingBlock />
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <THead>
              <TR>
                <TH>Карта</TH>
                <TH>Номер</TH>
                <TH>Бус</TH>
                <TH>Шофьор</TH>
                <TH>Валидна до</TH>
                <TH />
              </TR>
            </THead>
            <TBody>
              {(cards.data ?? []).length === 0 ? (
                <TableEmpty colSpan={6}>Още няма карти. Добавете картите AS 24 — по тях се свързват зарежданията.</TableEmpty>
              ) : (
                (cards.data ?? []).map((card) => {
                  const days = daysUntil(card.validUntil);
                  return (
                    <TR key={card.id} className={card.isActive ? undefined : 'opacity-60'}>
                      <TD>
                        <span className="font-medium">{PROVIDER_LABEL[card.provider] ?? card.provider.toUpperCase()}</span>
                        {card.label ? <span className="block text-[11px] text-ink-faint">{card.label}</span> : null}
                      </TD>
                      <TD className="font-mono text-xs" title={card.cardNumber}>
                        {masked(card.cardNumber)}
                      </TD>
                      <TD>{card.vehicle ?? <span className="text-warn">без бус</span>}</TD>
                      <TD className="text-ink-dim">{card.driverName ?? '—'}</TD>
                      <TD className="whitespace-nowrap">
                        {card.validUntil ? formatDate(card.validUntil) : '—'}
                        {!card.isActive ? (
                          <Badge tone="neutral" className="ml-1.5">спряна</Badge>
                        ) : days !== null && days < 0 ? (
                          <Badge tone="danger" className="ml-1.5">изтекла</Badge>
                        ) : days !== null && days <= 30 ? (
                          <Badge tone="warn" className="ml-1.5">след {days} дни</Badge>
                        ) : null}
                      </TD>
                      <TD className="text-right">
                        {canEdit ? (
                          <IconButton label="Редактирай картата" onClick={() => setEditing(card)}>
                            <Pencil className="size-3.5" />
                          </IconButton>
                        ) : null}
                      </TD>
                    </TR>
                  );
                })
              )}
            </TBody>
          </Table>
        </div>
      )}
      {editing ? <FuelCardDialog card={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </Card>
  );
}

function FuelCardDialog({ card, onClose }: { card: FuelCard | null; onClose: () => void }) {
  const save = useSaveFuelCard();
  const vehicles = useVehicles();
  const drivers = useDrivers();
  const [form, setForm] = useState({
    provider: card?.provider ?? 'as24',
    cardNumber: card?.cardNumber ?? '',
    label: card?.label ?? '',
    vehicleId: card?.vehicleId ?? '',
    driverId: card?.driverId ?? '',
    validUntil: card?.validUntil ?? '',
    isActive: card?.isActive ?? true,
    notes: card?.notes ?? '',
  });
  const valid = form.provider.length >= 2 && form.cardNumber.replace(/[\s-]/g, '').length >= 4;

  const submit = () =>
    save.mutate(
      {
        ...(card ? { id: card.id } : {}),
        body: {
          provider: form.provider,
          cardNumber: form.cardNumber,
          label: form.label.trim() || null,
          vehicleId: form.vehicleId || null,
          driverId: form.driverId || null,
          validUntil: form.validUntil || null,
          isActive: form.isActive,
          notes: form.notes.trim() || null,
        },
      },
      {
        onSuccess: () => {
          toast.ok(card ? 'Картата е записана' : 'Картата е добавена');
          onClose();
        },
        onError: (error) => toast.error('Картата не е записана', error instanceof Error ? error.message : undefined),
      },
    );

  return (
    <Dialog
      open
      onClose={onClose}
      title={card ? 'Горивна карта' : 'Нова горивна карта'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отказ
          </Button>
          <Button variant="primary" loading={save.isPending} disabled={!valid} onClick={submit}>
            Запиши
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Издател">
          <Select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} options={PROVIDERS} />
        </Field>
        <Field label="Номер на картата" required hint="Както е в извлечението — интервалите не са важни.">
          <Input className="font-mono" value={form.cardNumber} onChange={(e) => setForm({ ...form, cardNumber: e.target.value })} />
        </Field>
        <Field label="Бус">
          <Select
            value={form.vehicleId}
            onChange={(e) => setForm({ ...form, vehicleId: e.target.value })}
            placeholder="— без бус —"
            options={(vehicles.data?.items ?? []).map((v) => ({ value: v.id, label: `${v.fleetNo} · ${v.plate}` }))}
          />
        </Field>
        <Field label="Шофьор (ако е лична)">
          <Select
            value={form.driverId}
            onChange={(e) => setForm({ ...form, driverId: e.target.value })}
            placeholder="— не е лична —"
            options={(drivers.data?.items ?? []).map((d) => ({ value: d.id, label: d.fullName }))}
          />
        </Field>
        <Field label="Валидна до">
          <DatePicker withTime={false} value={form.validUntil} onChange={(e) => setForm({ ...form, validUntil: e.target.value })} />
        </Field>
        <Field label="Надпис">
          <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="например резервна" />
        </Field>
        <Field label="Бележка" className="sm:col-span-2">
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>
        <Checkbox
          className="sm:col-span-2"
          label="Активна — спрените не се мачват при импорт"
          checked={form.isActive}
          onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
        />
      </div>
    </Dialog>
  );
}
